import React, { useState } from 'react';
import { updateUserAttribute, confirmUserAttribute, sendUserAttributeVerificationCode } from 'aws-amplify/auth';
import { syncContact } from '../services/users';

interface Props {
  type: 'email' | 'phone';
  newValue: string;
  onClose: () => void;
  onVerified: () => void;
}

type Step = 'sending' | 'verify' | 'syncing' | 'done';

const ATTR_KEY = { email: 'email', phone: 'phone_number' } as const;

const ContactVerificationModal: React.FC<Props> = ({ type, newValue, onClose, onVerified }) => {
  const [step, setStep] = useState<Step>('sending');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const attrKey = ATTR_KEY[type];

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await updateUserAttribute({ userAttribute: { attributeKey: attrKey, value: newValue } });
        if (!cancelled) setStep('verify');
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to send verification code.');
          setStep('verify');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [attrKey, newValue]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await confirmUserAttribute({ userAttributeKey: attrKey, confirmationCode: code });
      setStep('syncing');
      await syncContact();
      setStep('done');
      setTimeout(() => onVerified(), 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: attrKey });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Verify {type === 'email' ? 'Email' : 'Phone'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {step === 'sending' && (
          <p className="text-sm text-gray-500">Sending verification code to <strong>{newValue}</strong>…</p>
        )}

        {(step === 'verify' || step === 'sending') && (
          <>
            {step === 'verify' && (
              <p className="text-sm text-gray-600 mb-4">
                We sent a verification code to <strong>{newValue}</strong>. Enter it below.
              </p>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  required
                  maxLength={6}
                  placeholder="123456"
                  autoFocus
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-center text-xl tracking-widest focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !code || step === 'sending'}
                className="w-full py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isLoading ? 'Verifying…' : `Verify ${type === 'email' ? 'Email' : 'Phone'}`}
              </button>
            </form>

            <div className="mt-4 text-center space-y-2">
              <button
                onClick={handleResend}
                disabled={isResending || step === 'sending'}
                className="text-sm text-green-600 hover:text-green-700 disabled:opacity-50"
              >
                {isResending ? 'Sending…' : 'Resend code'}
              </button>
              <div>
                <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'syncing' && (
          <p className="text-sm text-gray-500 text-center py-4">
            {type === 'email' ? 'Email' : 'Phone'} verified! Updating your profile…
          </p>
        )}

        {step === 'done' && (
          <p className="text-sm text-green-700 text-center py-4 font-medium">
            {type === 'email' ? 'Email address' : 'Phone number'} updated successfully.
          </p>
        )}
      </div>
    </div>
  );
};

export default ContactVerificationModal;
